import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import { fetchBlogPost } from '../services/blogService';
import { BlogPostDetail } from '../types/blog';
import { displayCategoryName } from '../utils/blogCategory';
import '../styles/blog-page.css';

function formatPublishedDate(dateIso: string | null): string {
    if (!dateIso) {
        return '';
    }
    const date = new Date(dateIso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function BlogDetailPage() {
    const { slug } = useParams<{ slug: string }>();
    const [post, setPost] = useState<BlogPostDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const loadPost = async () => {
            if (!slug) {
                setNotFound(true);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setNotFound(false);
            setLoadError(false);

            try {
                const result = await fetchBlogPost(slug);

                if (!isMounted) {
                    return;
                }

                if (result === null) {
                    setPost(null);
                    setNotFound(true);
                } else {
                    setPost(result);
                }
            } catch (error) {
                console.error('Error loading blog post:', error);
                if (!isMounted) {
                    return;
                }
                setPost(null);
                setLoadError(true);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadPost();

        return () => {
            isMounted = false;
        };
    }, [slug]);

    const sanitizedContent = useMemo(
        () => (post?.content ? DOMPurify.sanitize(post.content) : ''),
        [post?.content]
    );
    const categoryName = displayCategoryName(post?.categoryName);

    return (
        <main className="blog-page">
            <PublicHeader />

            <section className="blog-detail-section">
                <div className="blog-detail-inner">
                    {isLoading && <div className="blog-status">Loading post…</div>}

                    {!isLoading && loadError && (
                        <div className="blog-status blog-status-error">
                            Unable to load this post right now. Please try again later.
                        </div>
                    )}

                    {!isLoading && notFound && (
                        <div className="blog-status">
                            <p>This blog post could not be found.</p>
                            <Link to="/blogs" className="blog-back-link">
                                &larr; Back to Blog
                            </Link>
                        </div>
                    )}

                    {!isLoading && post && (
                        <article className="blog-detail">
                            {formatPublishedDate(post.publishedDate) && (
                                <p className="blog-detail-date">
                                    {formatPublishedDate(post.publishedDate)}
                                </p>
                            )}

                            <h1 className="blog-detail-title">{post.title}</h1>

                            {categoryName && (
                                <p className="blog-detail-meta">
                                    <span>{categoryName}</span>
                                </p>
                            )}

                            {post.imageUrl && (
                                <div className="blog-detail-image-wrap">
                                    <img
                                        src={post.imageUrl}
                                        alt={post.title}
                                        className="blog-detail-image"
                                    />
                                </div>
                            )}

                            {sanitizedContent && (
                                <div
                                    className="blog-detail-content"
                                    dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                                />
                            )}

                            {(post.prev || post.next) && (
                                <nav className="blog-detail-nav" aria-label="Adjacent posts">
                                    {post.prev ? (
                                        <Link
                                            to={`/blog/${post.prev.slug}`}
                                            className="blog-detail-nav-link blog-detail-nav-prev"
                                        >
                                            <span className="blog-detail-nav-arrow">&lt;</span>
                                            <span className="blog-detail-nav-title">{post.prev.title}</span>
                                        </Link>
                                    ) : (
                                        <span />
                                    )}
                                    {post.next ? (
                                        <Link
                                            to={`/blog/${post.next.slug}`}
                                            className="blog-detail-nav-link blog-detail-nav-next"
                                        >
                                            <span className="blog-detail-nav-title">{post.next.title}</span>
                                            <span className="blog-detail-nav-arrow">&gt;</span>
                                        </Link>
                                    ) : (
                                        <span />
                                    )}
                                </nav>
                            )}
                        </article>
                    )}
                </div>
            </section>

            <PublicFooter />
        </main>
    );
}
